import { DropdownComponent, DropdownOption } from "../../../../Components/AxForm/DropDownComponent";
import { WtpRentalPropertyRequestData } from "../../../../Dto/Rental/Properties/Request/WtpRentalPropertyRequestData";
import { GetRentalPropertiesResultDto } from "../../../../Dto/Rental/Properties/Result/GetRentalPropertiesResultDto";
import { RentalPropertyDto } from "../../../../Dto/Rental/Properties/Result/RentalPropertyDto";
import { RentalPropertyValueDto } from "../../../../Dto/Rental/Properties/Result/RentalPropertyValueDto";
import { I18nHelper, Translations } from "../../../../Lib/Helper/I18nHelper";
import { RandomHelper } from "../../../../Lib/Helper/RandomHelper";
import { AxLoading } from "../../../../Limestone/AxLoading/AxLoading";
import * as models from "../RiderDetailsModelBundle";
import { itemsData as ItemsData } from "../RiderDetailsModule";

const setSelectedProperties = (portlet) => (property) => {
    const retrievedProperty = portlet.allPropertiesToSelect.find((prop) => (
        prop.getWtpRentalPropertyRequestData().propertyId === property.propertyId
    ));
    if (retrievedProperty) {
        retrievedProperty.setValue(property.isStringValue
            ? property.propertyTextValue
            : property.propertyNumericValue.toString());
    }    
}
const capitalize = (str?: string) => str?.charAt(0).toUpperCase() + str?.slice(1);

export class RentalPropertiesSelection {
    private portlets: RentalPropertyPortlet[] = [];

    private prodRiderAndDetails: models.ProductAndRiderDetails;
    private i18nHelper = new I18nHelper();

    private arcNr: number;
    private durationId: number;
    private rentalStartSoapDateTime: string;

    private projNr: number;

    private allRentalProperties: RentalPropertyDto[] = [];
    private configRentalMandatoryProperties: boolean;

    constructor(prodRiderAndDetails: models.ProductAndRiderDetails, configRentalMandatoryProperties: boolean, item: ItemsData) {
        this.prodRiderAndDetails = prodRiderAndDetails;

        this.arcNr = prodRiderAndDetails.locationAndTimeSelection.selectedLocationId;
        this.durationId = prodRiderAndDetails.locationAndTimeSelection.durationId;
        this.rentalStartSoapDateTime = prodRiderAndDetails.locationAndTimeSelection.startDateTime;

        this.projNr = prodRiderAndDetails.productTripleWithType.projNr;
        this.configRentalMandatoryProperties = configRentalMandatoryProperties;

        let portlet: RentalPropertyPortlet;        
        if (this.prodRiderAndDetails.includedPackageItems?.length > 0) {
            this.prodRiderAndDetails.includedPackageItems.forEach((item) => {
                portlet = new RentalPropertyPortlet(
                    item.productId,
                    item.productName,
                    item.packagePosition
                );
                this.portlets = [...this.portlets, portlet];
                this.initialLoadAllRentalPropertiesAndShowDropDowns(portlet);
            });
        } else {
            portlet = new RentalPropertyPortlet(
                this.prodRiderAndDetails.productTripleWithType.id,
                item.name,
            );
            this.portlets = [...this.portlets, portlet];
            this.initialLoadAllRentalPropertiesAndShowDropDowns(portlet);
        }        
    }

    private createOnSelectCallback(portlet: RentalPropertyPortlet, propertyToSelect: PropertyToSelect): (selectedValue: string) => void {
        return (selectedValue: string) => {
            const property = propertyToSelect.getWtpRentalPropertyRequestData();
            // remove the selected property from the list to prevent duplicates
            //Packages
            if (this.prodRiderAndDetails.includedPackageItems.length > 0) {
                const packItem = this.prodRiderAndDetails.includedPackageItems.find(x => x.productId == portlet.itemId && x.packagePosition == portlet.packagePosition);
                packItem.selectedProperties = packItem.selectedProperties.filter((item) => (
                    item.propertyId !== property.propertyId
                ));
            }
            //Single Items            
            else {                
                this.prodRiderAndDetails.selectedProperties = this.prodRiderAndDetails.selectedProperties.filter((item) => (
                    item.propertyId !== property.propertyId
                ));    
            }            

            // if default value is selected reload without the selected property
            if (!selectedValue || selectedValue == '') {
                propertyToSelect.resetDropDownSelection();
                this.reloadAllRentalPropertiesAndShowDropDowns(portlet);                
                return;
            }

            // set the selected value to the property depending on the type
            if (property.isStringValue) {
                propertyToSelect.setStringValue(selectedValue);
            } else {
                propertyToSelect.setNumericValue(selectedValue);
            }

            // add the selected value to the list
            if (this.prodRiderAndDetails.includedPackageItems.length > 0) {
                const packItem = this.prodRiderAndDetails.includedPackageItems.find(x => x.productId == portlet.itemId && x.packagePosition == portlet.packagePosition);                
                if (packItem) {
                    packItem.selectedProperties = [
                        ...(packItem?.selectedProperties || []),
                        propertyToSelect.getWtpRentalPropertyRequestData(),
                    ];
                }
            } else {
                this.prodRiderAndDetails.selectedProperties = [
                    ...(this.prodRiderAndDetails?.selectedProperties || []),
                    propertyToSelect.getWtpRentalPropertyRequestData(),
                ];
            }

            this.reloadAllRentalPropertiesAndShowDropDowns(portlet);            
        };
    }

    private initialLoadAllRentalPropertiesAndShowDropDowns(portlet: RentalPropertyPortlet) {
        portlet.showLoadingSpinner();

        this.getAllRentalPropertiesForItem(portlet.itemId)
            .then((result) => {
                if (result.success === false || (result.success === true && result.rentalProperties.length === 0)) {
                    portlet.hideLoadingSpinner();
                    return;
                }

                portlet.renderPortlet(this.configRentalMandatoryProperties); 

                this.allRentalProperties = result.rentalProperties;
                result.rentalProperties?.forEach((property) => {
                    const requestData = new WtpRentalPropertyRequestData(
                        property.propertyId,
                        property.rentalPropertyValues[0].propertyType,
                        !!property.rentalPropertyValues[0].propertyStringValue,
                        property.rentalPropertyValues[0].propertyNumberValue,
                        property.rentalPropertyValues[0].propertyStringValue,
                    );

                    const propertySelection = new PropertyToSelect(requestData);
                    const options = propertySelection.createOptionsFromRentalPropertyValues(property.rentalPropertyValues);
                    const onSelectCallback = this.createOnSelectCallback(portlet, propertySelection);
                    const dropDown = new DropdownComponent(
                        `dropDown_${portlet.portletBody.id}-${property.propertyId}`,
                        portlet.portletBody.id,
                        property.propertyName,
                        options,
                        this.allRentalProperties,
                        onSelectCallback,
                        this.configRentalMandatoryProperties,
                    );                    
                    propertySelection.setInitalOptions(options);
                    propertySelection.setDropdownComponent(dropDown);
                    propertySelection.setActive();                    
                    portlet.allPropertiesToSelect = [...(portlet.allPropertiesToSelect || []), propertySelection];

                    // load for setting already selected properties
                    if (this.prodRiderAndDetails.selectedProperties.length > 0) {
                        this.setSelectedForSingle(portlet);
                    } else {
                        this.setSelectedForPackage(portlet);
                    }
                });
            })
            .finally(() => {
                this.checkNextButtonAvailability();
                portlet.hideLoadingSpinner();                
            });            
    }

    private setSelectedForSingle(portlet: RentalPropertyPortlet) {
        this.prodRiderAndDetails.selectedProperties?.forEach(setSelectedProperties(portlet));        
    }

    private setSelectedForPackage(portlet: RentalPropertyPortlet) {
        const packItem = this.prodRiderAndDetails.includedPackageItems.find((item)=> (
            item.productId === portlet.itemId && item.packagePosition === portlet.packagePosition)
        );

        if (!packItem || !packItem.selectedProperties || packItem.selectedProperties?.length === 0) {
            return;
        }

        packItem.selectedProperties?.forEach(setSelectedProperties(portlet));        
    }

    private reloadAllRentalPropertiesAndShowDropDowns(portlet: RentalPropertyPortlet) {
        portlet.showLoadingSpinner();

        this.getAllRentalPropertiesForItemFiltered(portlet.itemId)
            .then((result) => {
                // update the dropdowns/properties to select
                portlet.allPropertiesToSelect?.forEach((propertyToSelect) => {
                    const property = result.rentalProperties.find(x => x.propertyId == propertyToSelect.getWtpRentalPropertyRequestData().propertyId);                    
                    if (property) {
                        const options = propertyToSelect.createOptionsFromRentalPropertyValues(property.rentalPropertyValues);
                        propertyToSelect.upDateDropDownOptions(options);
                    } else {
                        propertyToSelect.upDateDropDownOptions([]);
                    }
                    
                });                
            })
            .finally(() => {                
                this.checkNextButtonAvailability();
                portlet.hideLoadingSpinner();
            });
    }

    private async getAllRentalPropertiesForItem(itemId: number): Promise<GetRentalPropertiesResultDto> {
        const url = `/api/GetRentalPropertiesApi/GetAllRentalPropertiesForItem`;
        const data = {
            ArcNr: this.arcNr,
            ProjNr: this.projNr,
            RentalItemNr: itemId,
            RentalStartSoapDateTime: this.rentalStartSoapDateTime,
            DurationId: this.durationId,
        };

        return new Promise<GetRentalPropertiesResultDto>((resolve) => {
            AxessViewer.ajaxRequest(url, data, (res) => {
                resolve(this.GetResultFromResponse(res));
            }, {
                type: 'POST',
                silent: true,
            });
        });
    }

    private async getAllRentalPropertiesForItemFiltered(itemId: number): Promise<GetRentalPropertiesResultDto> {
        const url = `/api/GetRentalPropertiesApi/GetAllRentalPropertiesForItemFiltered`;
        const data = {
            ArcNr: this.arcNr,
            ProjNr: this.projNr,
            RentalItemNr: itemId,
            RentalStartSoapDateTime: this.rentalStartSoapDateTime,
            DurationId: this.durationId,
            wtpRentalProperties: this.prodRiderAndDetails.selectedProperties,
        };

        return new Promise<GetRentalPropertiesResultDto>((resolve) => {
            AxessViewer.ajaxRequest(url, data, (res) => {
                resolve(this.GetResultFromResponse(res));
            }, {
                type: 'POST',
            });
        });
    }

    private GetResultFromResponse(res: any): GetRentalPropertiesResultDto {
        const getRentalPropertiesResult = new GetRentalPropertiesResultDto('', true);

        if (res.Success === false) {
            getRentalPropertiesResult.message = res.message;
            getRentalPropertiesResult.success = false;            
            return getRentalPropertiesResult;
        }

        getRentalPropertiesResult.rentalProperties = res.RentalProperties?.map((property) => {
            const rentalPropertyValues = property.RentalPropertyValues?.map((value) => (<RentalPropertyValueDto> {
                propertyType: value.PropertyType,
                propertyNumberValue: value.PropertyNumberValue,
                propertyStringValue: value.PropertyStringValue,
                showPropertyValue: value.ShowPropertyValue,
                isStringVal: value.PropertyStringValue,        
            }));
            return <RentalPropertyDto> {
                propertyId: property.PropertyId,
                propertyName: property.PropertyName,
                availableItems: property.AvailableItems,
                rentalPropertyValues,
            }
        });
        return getRentalPropertiesResult;
    }

    private checkNextButtonAvailability() {        
        const available = this.isPersonSelected() && this.areMandatoryProperties();
        $('.rental_detail_rider__btn-add_to_cart').prop("disabled", !available);
    }

    private isPersonSelected(): boolean {
        return this.prodRiderAndDetails.riderData?.customerAccountId !== undefined;        
    }

    private areMandatoryProperties(): boolean {        
        if (!this.configRentalMandatoryProperties) {
            return true;
        }

        const propertiesInDomToBeSet = $('[data-mandatory-property="true"]').length;
        if (propertiesInDomToBeSet === 0) {
            return true;
        }

        let setProperties = 0;        
        //Packages
        if (this.prodRiderAndDetails.includedPackageItems.length > 0) {            
            for (let i = 0; i < this.prodRiderAndDetails.includedPackageItems.length; i++) {
                if (this.prodRiderAndDetails.includedPackageItems[i].selectedProperties) {
                    setProperties += this.prodRiderAndDetails.includedPackageItems[i].selectedProperties.length;
                }                
            }            
        }
        //Single items
        else {
            setProperties = this.prodRiderAndDetails.selectedProperties.length            
        }   

        return setProperties === propertiesInDomToBeSet;
    }
}

class RentalPropertyPortlet {
    portlet: HTMLDivElement ;
    portletBody: HTMLDivElement;
    alert: HTMLDivElement;

    private axLoading: AxLoading;
    private i18nHelper = new I18nHelper();

    itemId: number;
    itemName: string;
    packagePosition?: number;

    allPropertiesToSelect: PropertyToSelect[] = [];

    constructor(itemId: number, itemName: string, packagePosition?: number) {
        this.axLoading = new AxLoading();
        this.itemId = itemId;
        this.itemName = itemName;
        this.packagePosition = packagePosition;
    }

    renderPortlet(mandatoryProperties: boolean = false) {
        const holder = document.getElementsByClassName('rental_detail_rider__right_container-rental-properties')[0];

        this.portlet = document.createElement('div');
        this.portlet.classList.add('ax-portlet', 'ax-portlet--md', 'ax-mt-2', 'rental-properties__portlet');
        holder.appendChild(this.portlet);

        this.portletBody = document.createElement('div');
        this.portletBody.classList.add('ax-portlet__body', 'rental-properties__portlet_body', 'position-relative');
        this.portletBody.id = RandomHelper.GenerateUniqueId();
        this.portlet.appendChild(this.portletBody);

        const header = document.createElement('h4');
        header.classList.add('ax-pb-2');
        const propertiesString = this.i18nHelper.GetTranslation(Translations.Properties);
        header.innerHTML = this.itemName ? `${this.itemName} ${propertiesString}` : capitalize(propertiesString);
        this.portletBody.appendChild(header);

        if (!mandatoryProperties) {
            this.alert = document.createElement('div');
            this.alert.classList.add('ax-alert');
            this.alert.classList.add('ax-alert--inline');
            this.alert.classList.add('ax-alert--success');
            this.alert.innerHTML = this.i18nHelper.GetTranslation(Translations.RentalYouCanSkipThisPart);
            this.portletBody.appendChild(this.alert);
        }        
    }

    setNoPropertiesAvailableAlert() {
        this.alert.innerHTML = `${this.itemName} - ${this.i18nHelper.GetTranslation(Translations.RentalNoProperties)}`;
        this.alert.classList.remove('ax-alert--success');
        this.alert.classList.add('ax-alert--info');
    }

    showLoadingSpinner() {
        this.axLoading.ShowLoadingIndicator(this.portletBody);
    }

    hideLoadingSpinner() {
        this.axLoading.HideLoadingIndicator(this.portletBody);
    }
}

class PropertyToSelect {
    constructor(wtpRentalPropertyRequestData: WtpRentalPropertyRequestData) {
        this.wtpRentalPropertyRequestData = wtpRentalPropertyRequestData;
    }

    private isActive: boolean = false;
    private dropdownComponent: DropdownComponent;
    private wtpRentalPropertyRequestData: WtpRentalPropertyRequestData;
    private i18nHelper = new I18nHelper();

    private initialOptions: DropdownOption[] = [];

    setNumericValue(value: string) {
        this.wtpRentalPropertyRequestData.propertyNumericValue = Number(value);
    }

    setInitalOptions(options: DropdownOption[]) {
        this.initialOptions = options;
    }

    setStringValue(value: string) {        
        this.wtpRentalPropertyRequestData.propertyTextValue = value;
    }

    setDropdownComponent(dropdownComponent: DropdownComponent) {
        this.dropdownComponent = dropdownComponent;
    }

    resetDropDownSelection() {
        this.dropdownComponent.resetDropDownSelection();
    }

    getWtpRentalPropertyRequestData(): WtpRentalPropertyRequestData {
        return this.wtpRentalPropertyRequestData;
    }

    createOptionsFromRentalPropertyValues(availableValues: RentalPropertyValueDto[]): DropdownOption[] {        
        const options: DropdownOption[] = availableValues.map((value) => (            
            new DropdownOption(
                value.showPropertyValue, value.isStringVal
                    ? value.propertyStringValue
                    : value.propertyNumberValue.toString()
            )
        ));
        options.unshift(this.getDefaultOption());
        return options;
    }

    upDateDropDownOptions(options: DropdownOption[]) {
        this.dropdownComponent.updateOptions(options, this.initialOptions);
    }

    setValue(value: string) {
        this.dropdownComponent.setValue(value);
    }

    setActive(): void {
        this.isActive = true;
        this.dropdownComponent.showDropdown();
    }

    setInactive(): void {
        this.isActive = false;
        this.dropdownComponent.hideDropdown();
    }

    private getDefaultOption(): DropdownOption {
        const defaultOption = new DropdownOption(this.i18nHelper.GetTranslation(Translations.ResetSelection), 'select');
        return defaultOption;
    }
}